/**
 * weatherConfig — pure target-Y selection for region-aware weather streams
 * (scene-schema T7). No Pixi; just geometry over `ctx.scene.regions`.
 *
 * A region-spanning RISE stream (bubbles/embers/motes) normally emits from the
 * camera region's BOTTOM edge and rises off the top of the screen. When the
 * level authors a tagged spatial volume (e.g. a `'water'` region), the stream
 * should instead emit from — and rise toward — that volume's TOP edge, so
 * underwater bubbles break at the water surface rather than the screen edge.
 *
 * `regionTopY(regions, kind)` finds the highest-priority matching region's top
 * world-Y (the min `bounds.y` if several share a kind); null when absent.
 * `riseOriginY(region, regions)` is the resolved emit Y for a rise stream:
 * the water surface when present (clamped into the camera region so it never
 * emits off-screen), else the camera region bottom — preserving prior behaviour.
 */
import type { SceneRegion } from '../../../../shared/SceneTypes';
import type { Region } from '../atmospherePlacement';

/** Top world-Y (min `bounds.y`) of the first matching tagged region; null if none. */
export function regionTopY(regions: ReadonlyArray<SceneRegion>, kind: string): number | null {
  let top: number | null = null;
  for (const r of regions) {
    if (r.kind !== kind) continue;
    if (top === null || r.bounds.y < top) top = r.bounds.y;
  }
  return top;
}

/** Emit/extent Y for a region-spanning RISE stream: the `'water'` surface top
 *  (clamped into the camera region so the band stays on-screen) when a water
 *  region is authored; otherwise the camera region's BOTTOM edge (prior behaviour). */
export function riseOriginY(region: Region, regions: ReadonlyArray<SceneRegion>): number {
  const water = regionTopY(regions, 'water');
  if (water === null) return region.y1;
  return Math.min(Math.max(water, region.y0), region.y1);
}
